/**
 * AcceptInvitePage — public landing for /accept-invite?token=...
 * Flow:
 *   1. Look up invite via GET /api/team/invitations/:token
 *   2. Show org + inviter, prompt for full_name + password
 *   3. POST /api/team/invitations/:token/accept — server creates user + session
 *   4. Mark auth as authenticated and navigate to /dashboard
 */
import { useEffect, useRef, useState } from 'react';
import { AuthLayout } from '../layout/AuthLayout.jsx';
import { FormField } from '../components/FormField.jsx';
import { TextInput } from '../components/TextInput.jsx';
import { PasswordInput } from '../components/PasswordInput.jsx';
import { SubmitButton } from '../components/SubmitButton.jsx';
import { FormError } from '../components/FormError.jsx';
import { api, ApiError } from '../api/client.js';

export function AcceptInvitePage({ navigate, token, auth }) {
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const firstRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setLoadError(new Error('Missing invitation token.'));
      return;
    }
    let cancelled = false;
    api.getInvitation(token)
      .then((data) => { if (!cancelled) setInvite(data?.invitation || null); })
      .catch((e) => { if (!cancelled) setLoadError(e); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => { if (invite) firstRef.current?.focus(); }, [invite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      const resp = await api.acceptInvitation(token, {
        full_name: fullName.trim(),
        password,
      });
      if (resp?.user && resp?.organization && auth?.setAuthenticated) {
        auth.setAuthenticated(resp.user, resp.organization);
      }
      navigate('/dashboard');
    } catch (e2) {
      if (e2 instanceof ApiError && e2.fieldErrors) {
        setFieldErrors(e2.fieldErrors);
      }
      setSubmitError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    const msg = loadError instanceof ApiError && loadError.message
      ? loadError.message
      : 'This invitation link is invalid or has expired.';
    return (
      <AuthLayout title="Invitation unavailable">
        <p className="form-error" role="alert">{msg}</p>
        <div className="auth-links">
          <a href="/login" onClick={(ev) => { ev.preventDefault(); navigate('/login'); }}>
            Return to log in
          </a>
        </div>
      </AuthLayout>
    );
  }

  if (!invite) {
    return (
      <AuthLayout title="Loading invitation…">
        <p className="form-loading" role="status">Looking up your invitation.</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${invite.org_name}`}
      subtitle={`Invited by ${invite.inviter_name || 'a teammate'} · ${invite.invited_email}`}
    >
      <form onSubmit={handleSubmit} noValidate>
        <FormError error={submitError} />
        <FormField label="Full name" htmlFor="accept-name" error={fieldErrors.full_name}>
          <TextInput
            id="accept-name"
            name="full_name"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            ref={firstRef}
            error={!!fieldErrors.full_name}
            required
          />
        </FormField>
        <FormField
          label="Choose a password"
          htmlFor="accept-password"
          hint="At least 10 characters, with a letter and a digit."
          error={fieldErrors.password}
        >
          <PasswordInput
            id="accept-password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!fieldErrors.password}
            required
          />
        </FormField>
        <SubmitButton loading={submitting}>Accept invitation</SubmitButton>
      </form>
    </AuthLayout>
  );
}
